/**
 * SearchInput Component
 *
 * Premium search input with glass morphism effect.
 * Features focus animations, keyboard event handling, and clean styling.
 */

import { SearchOutlined } from '@ant-design/icons';
import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { toEnglishLayout } from '@/shared/utils';

import { MAX_SEARCH_QUERY_LENGTH, SEARCH_DEBOUNCE_MS } from '../types';

interface SearchInputProps {
  /** Current search value */
  value: string;

  /** Callback when search value changes (debounced) */
  onChange: (value: string) => void;

  /** Callback for keyboard events (ArrowUp/Down/Enter/Escape) */
  onKeyDown: (e: React.KeyboardEvent) => void;

  /** Whether dropdown is open (for autoFocus logic) */
  isOpen: boolean;

  /** ID of currently focused item for aria-activedescendant */
  activedescendant?: string;

  /** Whether to autoFocus the input */
  autoFocus?: boolean;

  /** Optional ref for programmatic focus (e.g., type-to-open) */
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  onKeyDown,
  isOpen,
  activedescendant,
  autoFocus = false,
  inputRef,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Track the last value sent via onChange to avoid duplicate calls
  const lastSentValueRef = useRef(value);

  // Sync external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Store onChange in ref to avoid resetting debounce timer when callback changes
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Debounce onChange calls - only depend on localValue, not onChange
  useEffect(() => {
    // Skip if this value was already sent (e.g., from handleClear)
    if (localValue === lastSentValueRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      lastSentValueRef.current = localValue;
      onChangeRef.current(localValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [localValue]);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // Convert Cyrillic characters to English QWERTY layout equivalents
    // This helps users who forget to switch keyboard layout when searching
    const newValue = toEnglishLayout(rawValue);
    // Enforce max length (defense in depth - HTML maxLength can be bypassed via JS)
    if (newValue.length <= MAX_SEARCH_QUERY_LENGTH) {
      setLocalValue(newValue);
    }
  }, []);

  // Handle clear button - immediate feedback, skip debounce
  const handleClear = useCallback(() => {
    setLocalValue('');
    lastSentValueRef.current = '';
    onChange('');
  }, [onChange]);

  return (
    <div
      className={cn(
        'relative flex items-center gap-3 rounded-xl border px-4 py-3 transition-all duration-200',
        isFocused
          ? 'border-white/20 bg-white/10 shadow-[0_0_0_2px_rgba(99,102,241,0.2)]'
          : 'border-white/10 bg-white/5 hover:bg-white/8',
      )}
    >
      <SearchOutlined
        className={cn(
          'text-lg transition-colors duration-200',
          isFocused ? 'text-indigo-400' : 'text-gray-500',
        )}
        aria-hidden="true"
      />

      <input
        id="pair-search-input"
        type="text"
        value={localValue}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        ref={inputRef}
        placeholder="Search pairs, projects, arbitrages..."
        maxLength={MAX_SEARCH_QUERY_LENGTH}
        autoFocus={autoFocus}
        spellCheck={false}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls="search-results-listbox"
        aria-activedescendant={activedescendant}
        aria-label="Search pairs, projects, and arbitrages"
        className="flex-1 bg-transparent text-sm font-medium text-white placeholder:text-gray-500 focus:outline-none"
      />

      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClear();
            }
          }}
          className="flex h-5 w-5 items-center justify-center rounded-full bg-white/10 text-xs text-gray-400 transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus-visible:bg-white/30"
          aria-label="Clear search"
        >
          <span aria-hidden="true">✕</span>
        </button>
      )}
    </div>
  );
};
